// client/src/services/lexistarStatusBoard.js
//
// LexiStar control-tower API client (ST-00188, M2-U: T6-S04 status board,
// T6-S06 plan & dispatch, T6-S07 approvals queue, trip detail). Server:
// server/modules/lexistar/trips + events (M2-A2, contracts m2-contracts.md
// §4.2) and, for map geometry and reason codes, the routes module (M2-A1, §4.1).
//
// Thin wrapper over the shared axios instance in `services/api` (cookie-session
// auth, CSRF header, single-flight 401 refresh; `api` already carries `/api`).
// Every helper resolves to the response BODY. Payloads use the DB column names
// (snake_case) of m2-contracts §3.
//
// Action routes use the contract's `:verb` suffix literally
// (POST /trips/:id:dispatch, POST /deviations/:id:approve, …).
//
// Realtime: subscribeLexiStarEvents() joins the Socket.IO /tracking namespace
// and forwards `lexistar:event` (EventV1) for the caller's tenant only (§2.4).
//
// The response shapes the pages code against are written out below as
// JSDoc typedefs — the UI's reading of §4.2 (which names the content, not the
// field names), since adopted by M2-A2's server (see services/lexistarTrips.js
// for the server-side list) and followed by the page tests' fixtures. Request
// bodies are validated with Joi `stripUnknown`, so a field the server does not
// know is dropped silently — the ones the UI sends ahead of the API are marked.

import { io } from 'socket.io-client';
import api from './api';

const LX = '/lexistar';

/** Drop undefined / null / '' so the API never sees `stage=`. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

const body = (res) => res.data;

/**
 * First array found under `keys` (or the body itself when it is an array).
 * Tolerates `{ rows }`, `{ data }` and bare-array bodies.
 */
export const pickList = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  for (const k of [...keys, 'data', 'rows', 'items']) {
    if (Array.isArray(data[k])) return data[k];
  }
  return [];
};

// ── Types (the UI's contract reading) ───────────────────────────────────────

/**
 * @typedef {'measured'|'derived'|'estimated'} Provenance  (case-insensitive; LS-09 §3)
 *
 * @typedef {Object} OverConsumption  LS-04 §9 flag
 * @property {number} excess_l
 * @property {'medium'|'high'} severity
 * @property {'confirmed'|'indicative'} certainty
 *
 * @typedef {Object} StatusBoardRow  GET /status-board → { rows: StatusBoardRow[], generated_at }
 * @property {string} asset_key          'reg:JX12KLGP'
 * @property {string|null} asset_id
 * @property {string|null} registration   null for an engine asset not in the register
 * @property {string|null} fleet_no
 * @property {string|null} driver_name    (no driver register in M2: null; the UI falls back to driver_tag)
 * @property {string|null} [driver_tag]
 * @property {string|null} [last_update]  when the engine last saw the asset
 * @property {Object|null} [next_trip]    the next PLANNED trip when there is no active one
 * @property {{motion:string, context:string, overlays:string[], since:string|null,
 *             override?: {stage:string, reason:string, until:string|null}|null}} status
 * @property {{ts:string, lat:number, lon:number, speed_kmh:number|null, heading:number|null,
 *             speed_limit_kmh?:number|null, place?:string|null, provisional?:boolean}|null} position
 * @property {{id:string, reference:string, stage:string, stage_since:string|null,
 *             route:{id:string, code:string, name:string, version:number, status?:string}|null,
 *             from_name:string, to_name:string, planned_km:number, adjusted_km:number|null,
 *             km_travelled:number|null, pct_distance:number|null, km_remaining:number|null,
 *             distance_provenance?:Provenance, route_avg_km:number|null,
 *             above_route_norm?:boolean, eta:string|null, eta_delta_min?:number|null}|null} trip
 * @property {{budget_original_l:number, budget_adjusted_l:number|null, consumed_l:number|null,
 *             pct_budget:number|null, burn_index:number|null, projected_total_l:number|null,
 *             over_under_l:number|null, over_under_r?:number|null, provenance:Provenance,
 *             over_consumption?:OverConsumption|null}|null} fuel
 * @property {{id:string, trip_seq:number, trip_count:number, loads_completed:number,
 *             litres_used:number, litres_allowance:number,
 *             next_trip?:{id:string, reference:string, from_name:string, to_name:string}|null}|null} run
 *
 * @typedef {Object} RunSummary  GET /runs/:id/summary, and each entry of
 *   GET /status-board?view=run → { runs: RunSummary[] }
 * @property {{id:string, asset_key:string, registration:string, driver_name:string|null,
 *             shift_date:string, state:'open'|'closed'}} run
 * @property {Array<{id:string, reference:string, seq:number, stage:string, origin:string,
 *             from_location_id:number, from_name:string, to_location_id:number, to_name:string,
 *             material:string|null, planned_tonnes:number|null, net_tonnes?:number|null,
 *             budget_l:number|null, consumed_l:number|null, provenance:Provenance,
 *             over_consumption?:OverConsumption|null, flags?:string[]}>} trips
 * @property {Array<{after_seq:number, site_name:string, offloading_min:number|null,
 *             waiting_min:number|null, loading_min:number|null}>} [turnarounds]
 * @property {{loads:number, tonnes:number, loaded_km:number, empty_km:number,
 *             empty_running_pct:number|null, litres_used:number, litres_allowance:number}} totals
 * @property {{location_id:number, name:string}|null} [next_source]  default source for
 *             *Next load from here* (the truck's current or next offloading site)
 *
 * @typedef {Object} DeviationRow  GET /deviations?state= → { deviations: DeviationRow[] }
 *   The deviations table (§3.2) plus trip context, the latest position, and —
 *   UI assumption, not in §4.2 — the off-route `track` (GeoJSON LineString).
 * @property {string} id
 * @property {number} tenant_id
 * @property {string} trip_id
 * @property {string} state
 * @property {string} opened_at
 * @property {number|null} max_offset_m
 * @property {number|null} off_route_km
 * @property {number|null} duration_s
 * @property {'I'|'L'|'M'|'H'|'C'} severity
 * @property {number|null} confidence
 * @property {{code:string, text?:string|null, photo_url?:string|null, replied_at?:string}|null} driver_reason
 * @property {string|null} [sla_due_at]
 * @property {Object} trip        { id, reference, stage, registration, driver_name,
 *                                  route:{id,code,name,version}, from_name, to_name,
 *                                  load:{material, order_ref}, planned_km, adjusted_km,
 *                                  budget_adjusted_l }
 * @property {{ts:string, lat:number, lon:number, speed_kmh?:number, heading?:number}|null} position
 * @property {{type:'LineString', coordinates:number[][]}|null} [track]
 * @property {boolean} [can_decide]  false when another organisation owns the trip
 */

// ── Status board ─────────────────────────────────────────────────────────────

/** { rows: StatusBoardRow[], generated_at } — or { runs: RunSummary[] } with view:'run'. */
export const getStatusBoard = (params) =>
  api.get(`${LX}/status-board`, { params: clean(params) }).then(body);

// ── Trips ────────────────────────────────────────────────────────────────────

/** { trips, pagination? } — filters: stage (comma list), asset, from, to, run. */
export const listTrips = (params) => api.get(`${LX}/trips`, { params: clean(params) }).then(body);

/**
 * { trip (trip_view row with legs), timeline, deviations, ticket_matches, fuel,
 *   track? (UI assumption: actual track as GeoJSON LineString) }
 */
export const getTrip = (id) => api.get(`${LX}/trips/${id}`).then(body);

/** { trip } — PLANNED; the server computes legs and budget_original_l. */
export const createTrip = (data) => api.post(`${LX}/trips`, data).then(body);

/**
 * { created, trips } — all or nothing: a 4xx names the failing index. Per trip
 * the UI also sends `channel` and `budget_override: { litres, reason }` (LS-09
 * §5.8), which the M2 API does not accept yet (stripped).
 */
export const createTripsBatch = (trips) => api.post(`${LX}/trips:batch`, { trips }).then(body);

/** { trip } — stage → DISPATCHED. */
export const dispatchTrip = (id) => api.post(`${LX}/trips/${id}:dispatch`, {}).then(body);

export const cancelTrip = (id, reason) =>
  api.post(`${LX}/trips/${id}:cancel`, { reason }).then(body);

export const abortTrip = (id, reason) => api.post(`${LX}/trips/${id}:abort`, { reason }).then(body);

/**
 * Manual override (lexistar.trips.manage). The M2 API takes { stage, reason };
 * `until` (LS-09 §5.3) is sent ahead of the API and stripped until it lands.
 * @param {string} id trip id
 * @param {{stage:string, reason:string, until?:string}} data
 */
export const overrideStage = (id, data) =>
  api.post(`${LX}/trips/${id}:overrideStage`, clean(data)).then(body);

/** Retrospective trip from a detected load (origin 'prompted'). { trip } */
export const createTripFromDetectedLoad = (data) =>
  api.post(`${LX}/trips:fromDetectedLoad`, clean(data)).then(body);

// ── Runs ─────────────────────────────────────────────────────────────────────

/** { run } */
export const createRun = (data) => api.post(`${LX}/runs`, clean(data)).then(body);

/**
 * *Next load from here*: { trip, legs, run } appended to the run. The API
 * dispatches by default; the UI sends dispatch:false so the allowance is shown
 * before the dispatcher sends it.
 */
export const appendTripToRun = (runId, data) =>
  api.post(`${LX}/runs/${runId}:appendTrip`, clean(data)).then(body);

/** RunSummary */
export const getRunSummary = (runId) => api.get(`${LX}/runs/${runId}/summary`).then(body);

// ── Deviations (approvals queue) ─────────────────────────────────────────────

/** { deviations: DeviationRow[] } — state is a comma list, e.g. 'OPEN,AWAITING_DECISION'. */
export const listDeviations = (params) =>
  api.get(`${LX}/deviations`, { params: clean(params) }).then(body);

/**
 * @param {string} id
 * @param {{reason_code:string, create_corridor?:{scope:'trip'|'route', expiry:string},
 *          extra_km?:number}} data
 */
export const approveDeviation = (id, data) =>
  api.post(`${LX}/deviations/${id}:approve`, clean(data)).then(body);

export const rejectDeviation = (id, data) =>
  api.post(`${LX}/deviations/${id}:reject`, clean(data)).then(body);

export const escalateDeviation = (id, data) =>
  api.post(`${LX}/deviations/${id}:escalate`, clean(data)).then(body);

// ── Events ───────────────────────────────────────────────────────────────────

/** { events: EventV1[], pagination? } — FR-EVT-002 filters (type, status, asset, from, to). */
export const listEvents = (params) => api.get(`${LX}/events`, { params: clean(params) }).then(body);

export const acknowledgeEvent = (id) => api.post(`${LX}/events/${id}:acknowledge`, {}).then(body);

// ── Reference data (M2-A1 routes module, M1 assets, existing locations API) ──

/** { reason_codes: [{ id, kind, code, label, active, sort }] } */
export const listReasonCodes = (kind) =>
  api.get(`${LX}/reason-codes`, { params: clean({ kind }) }).then(body);

/** { lanes: [{ id, from_location_id, to_location_id, from_name?, to_name?, name }] } */
export const listLanes = (params) => api.get(`${LX}/lanes`, { params: clean(params) }).then(body);

/** { routes: [{ id, lane_id, code, name, status, current_version, planned_km?, planned_min? }] } */
export const listRoutes = (params) => api.get(`${LX}/routes`, { params: clean(params) }).then(body);

/** { route, versions: [{ version, geometry, planned_km, planned_min, corridor_m }] } */
export const getRoute = (id) => api.get(`${LX}/routes/${id}`).then(body);

/** Corridor polygon for display: a GeoJSON Feature or Polygon (or { corridor }). */
export const getRouteCorridor = (id, version) =>
  api.get(`${LX}/routes/${id}/versions/${version}/corridor`).then(body);

/** Sites: { success, count, locations: [{ location_id, location_name }] } */
export const listSites = () => api.get('/locations').then(body);

// ── Realtime (§2.4) ──────────────────────────────────────────────────────────

export const LEXISTAR_EVENT = 'lexistar:event';

const socketBaseUrl = () =>
  import.meta.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5002';

const SEEN_LIMIT = 500;

/**
 * Join the /tracking namespace and forward `lexistar:event` for one tenant.
 *
 * Rooms (M2-A2 relay, server/modules/lexistar/events/relay.js — each join is
 * checked against the socket's own session): `lexistar:join` → tenant:{id},
 * `join:approvals` → tenant:{id}:approvals (ROUTE.DEVIATION_*), `join:trip` →
 * trip:{id} (only when the trip is visible). Events for any other tenant (or,
 * with `tripId`, any other trip) are dropped, and an event delivered through
 * two rooms is passed on once.
 *
 * Never joins without a tenant id — there is no default tenant.
 *
 * @param {{tenantId:number|string|null, approvals?:boolean, tripId?:string|null,
 *          onEvent:(evt:Object)=>void, onStatus?:(s:'live'|'reconnecting'|'offline')=>void}} opts
 * @returns {() => void} unsubscribe
 */
export function subscribeLexiStarEvents({
  tenantId,
  approvals = false,
  tripId = null,
  onEvent,
  onStatus,
}) {
  if (tenantId === null || tenantId === undefined || tenantId === '') return () => {};

  const socket = io(`${socketBaseUrl()}/tracking`, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
  });

  const seen = new Set();
  const join = () => {
    socket.emit('lexistar:join', { tenantId });
    if (approvals) socket.emit('join:approvals', { tenantId });
    if (tripId) socket.emit('join:trip', { tripId });
    onStatus?.('live');
  };
  const handler = (evt) => {
    if (!evt || typeof evt !== 'object') return;
    if (String(evt.tenant_id) !== String(tenantId)) return;
    if (tripId && evt.trip_id !== tripId) return;
    if (evt.id) {
      if (seen.has(evt.id)) return;
      seen.add(evt.id);
      if (seen.size > SEEN_LIMIT) seen.delete(seen.values().next().value);
    }
    onEvent(evt);
  };
  const onDisconnect = () => onStatus?.('reconnecting');
  const onError = () => onStatus?.('offline');

  socket.on('connect', join);
  socket.on('disconnect', onDisconnect);
  socket.on('connect_error', onError);
  socket.on(LEXISTAR_EVENT, handler);

  return () => {
    socket.off('connect', join);
    socket.off('disconnect', onDisconnect);
    socket.off('connect_error', onError);
    socket.off(LEXISTAR_EVENT, handler);
    socket.disconnect();
  };
}
